import {
  AllStructuresForUserManagementQuery,
  useAllStructuresForUserManagementQuery,
} from '@/__generated__/graphql';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { NavigationListItem } from '@/types';
import { Box, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlatformDetails } from './PlatformDetails';
import { NavigationPanel } from '../shared/NavigationPanel';
import { PageLoader } from '@/components/shared/PageLoader';
import { PanelBox } from '../styles';

type Structure = AllStructuresForUserManagementQuery['allStructures'][0];

export const PlatformPanel = () => {
  const { loading, data: userPlatformData } = useAllStructuresForUserManagementQuery();
  const [selectedPlatform, setSelectedPlatform] = useState<Structure | undefined>(undefined);
  const { leftDrawerWidth } = useDrawerWidths();

  useEffect(() => {
    const { allStructures } = userPlatformData || {};
    if (!loading && allStructures?.length && !selectedPlatform) {
      setSelectedPlatform(allStructures[0]);
    }
    if (!loading && allStructures?.length && selectedPlatform) {
      setSelectedPlatform(allStructures.find((platform) => platform.id === selectedPlatform.id));
    }
  }, [userPlatformData, loading, selectedPlatform, setSelectedPlatform]);

  const handlePlatformClick = useCallback(
    (platform: Structure) => {
      setSelectedPlatform(platform);
    },
    [setSelectedPlatform]
  );

  const items = useMemo(() => {
    return (userPlatformData?.allStructures ?? [])
      .map((platform) => {
        if (platform) {
          const item: NavigationListItem = {
            id: platform.id,
            value: platform.name,
            label: platform.name,
            count: platform?.userGroups.length,
            handleClick: () => {
              handlePlatformClick(platform);
            },
          };
          return item;
        }
        return undefined;
      })
      .filter((platform): platform is NavigationListItem => !!platform);
  }, [handlePlatformClick, userPlatformData?.allStructures]);

  if (loading) {
    return (
      <Box sx={{ position: 'relative', top: '107px' }}>
        <PageLoader />
      </Box>
    );
  }

  return (
    <Stack direction="row" height="100%">
      <NavigationPanel
        itemType="Platform"
        items={items}
        selectedItemId={selectedPlatform?.id ?? ''}
      />
      <PanelBox width={`calc(100vw - ${leftDrawerWidth}px)`}>
        <PlatformDetails platform={selectedPlatform} />
      </PanelBox>
    </Stack>
  );
};
